/* eslint-disable no-return-assign */
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { hashHistory } from 'react-router';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
// HOCs
import withStateReinit from 'hocs/WithStateReinit';
import withTitle from 'hocs/WithTitle';
// Models
import mFinance from 'models/cpm/finance';
import { queGetMyBooking, queGetUpdatedMyBookingPurchases } from 'models/cpm/booking';
import { queGetOnDemandAccess } from 'models/cpm/onDemand';
// Custom
import { cpmGetMyBooking, cpmGetUpdatedMyBooking, cpmGetOnDemandAccess } from 'actions';
import {
  cpmBookingDataShape,
  cpmMyBookingPurchasesShape,
  cpmOnDemandAccessForCheckoutDataShape,
} from 'constants/PropTypesShapes';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';
import Error404 from 'error404/components/cpm_client/404';
import { navigateToBook } from 'cpmBook/navigation';
import PurchasePayment from 'purchase/assets/PurchasePayment';
import { navigateToExtendMyBooking, navigateToRegularizeMyBooking } from 'cpmMyBookings/navigation';
import { navigateToCheckout } from 'cpmCheckout/navigation';

const Purchase = ({
  location: { query },

  cpmProduct,
  cpmProductLoading,

  cpmUpdatedMyBooking,

  dispatchGetMyBooking,
  dispatchGetOnDemandAccess,
  dispatchGetUpdatedMyBooking,
}) => {
  const [loadingBeforeRedirection, setLoadingBeforeRedirection] = React.useState(false);
  const purchase = cpmProduct && mFinance.getPurchaseById(cpmProduct.purchases, query.id);

  // 1️⃣ Retrieve product thanks to query params
  /* ------------------------------------------ */
  const productType = mFinance.findPurchasableProduct(query);

  const productReference = query[productType.queryParam];

  const productIsBooking = mFinance.productIsBooking(productType);
  const productIsBookingExtension = mFinance.productIsBookingExtension(productType);
  const productIsBookingOverstay = mFinance.productIsBookingOverstay(productType);
  const productIsBookingType = productIsBooking || productIsBookingExtension || productIsBookingOverstay;

  const productIsOnDemand = mFinance.productIsOnDemand(productType);
  const productIsOnDemandType = productIsOnDemand;
  // ⬇️⬇️
  /* It is important to retrieve product from query params even if it was still previously loaded because its purchases may have been updated during a previous operation just before redirection (such as extend) */
  React.useEffect(function retrieveProductFromQueryParams() {
    if (productType) {
      if (productIsBookingType) dispatchGetMyBooking({ reference: productReference });
      if (productIsOnDemandType) dispatchGetOnDemandAccess({ reference: productReference });
    }
  }, []);

  // 2️⃣ After payment success, we redirect to the appropriate page or we fetch repeatedly updated information about booking
  /* ----------------------------------------------------------------------------------------------------------------------- */

  const globalBookingRefreshTimeoutId = React.useRef(null);
  const currentBookingRefreshTimeoutId = React.useRef(null);
  // This will only get purchases to be sure last purchase is marked as paid after payment completion
  const refreshMyBookingInStore = () => {
    const DELAY_BEFORE_REQUESTING_AGAIN = 500;
    currentBookingRefreshTimeoutId.current = setTimeout(
      () => dispatchGetUpdatedMyBooking({ reference: cpmProduct.reference }),
      DELAY_BEFORE_REQUESTING_AGAIN,
    );
  };

  const paymentIsSuccessful = React.useRef(false);
  const handlePaymentSuccess = () => {
    paymentIsSuccessful.current = true;

    // [BOOKING]
    // We try to fetch updated information (it will do it again in useEffect until it is ok) and
    // we initiate a global timeout to stop fetching after a certain delay

    if (productIsBookingType) {
      setLoadingBeforeRedirection(true);
      refreshMyBookingInStore();

      const DELAY_BEFORE_TIMEOUT = 5000;
      globalBookingRefreshTimeoutId.current = setTimeout(() => {
        const myBookingShowUrl = `${productType.paymentOptions.successURI}/${cpmProduct.reference}?operation=${productType.paymentOptions.operationSuccess}`;
        hashHistory.push(myBookingShowUrl);
      }, DELAY_BEFORE_TIMEOUT);
    }

    // [ON DEMAND]
    // We redirect to on demand success message page, no need to fetch data again
    if (productIsOnDemandType) {
      const nextUrl = `${productType.paymentOptions.successURI}/${cpmProduct.reference}?operation=${productType.paymentOptions.operationSuccess}`;
      hashHistory.push(nextUrl);
    }
  };
  // ⬇️⬇️
  // [FOR BOOKING ONLY] We redirect once booking purchase is marked as paid
  React.useEffect(
    function redirectAfterPaymentSuccessfulAndBookingUpdated() {
      if (cpmUpdatedMyBooking && paymentIsSuccessful.current) {
        const lastPurchase = mFinance.lastPurchase(cpmUpdatedMyBooking);
        const lastPurchaseIsCurrentPurchase = String(lastPurchase.id) === query.id;
        const lastPurchaseMarkedAsPaid = mFinance.lastPurchasePaymentIsPaid(cpmUpdatedMyBooking);
        const isReadyToRedirect = lastPurchaseIsCurrentPurchase && lastPurchaseMarkedAsPaid;

        if (!isReadyToRedirect) {
          // We try again to refresh my booking information until purchase returned by server is marked as paid (it takes some time)
          clearTimeout(currentBookingRefreshTimeoutId.current);
          refreshMyBookingInStore();
          return;
        }

        clearTimeout(globalBookingRefreshTimeoutId.current);
        const nextUrl = `${productType.paymentOptions.successURI}/${cpmProduct.reference}?operation=${productType.paymentOptions.operationSuccess}`;
        hashHistory.push(nextUrl);
      }
    },
    [cpmUpdatedMyBooking],
  );

  // 3️⃣ If payment is cancelled or can't be initiated, we redirect to the appropriate page
  /* ----------------------------------------------------------------------------------------------------------------------- */
  function redirectToInitialPage() {
    const paymentCancelledError = { error: 'paymentCancelled' };

    if (productIsBooking) navigateToBook(cpmProduct.carpark.id, paymentCancelledError);
    if (productIsBookingExtension) navigateToExtendMyBooking(cpmProduct.reference, paymentCancelledError);
    if (productIsBookingOverstay) navigateToRegularizeMyBooking(cpmProduct.reference, paymentCancelledError);

    if (productIsOnDemand) navigateToCheckout(paymentCancelledError);
  }

  const purchaseIsCancelled = mFinance.purchasePaymentIsCancelled(purchase);
  if (purchaseIsCancelled) redirectToInitialPage();

  /* ========================================================= */
  /* UI                                                    */
  /* ========================================================= */

  /* eslint-disable */
  // prettier-ignore
  const PurchaseWrapper = productIsBookingType  ? require('purchase/assets/PurchaseWrapperBooking') :
                          productIsOnDemandType ? require('purchase/assets/PurchaseWrapperOnDemand') :
                          null;
  /* eslint-enable */
  // prettier-ignore
  if (!productType) return null;

  return (
    <LoadingDisplayer loading={cpmProductLoading || loadingBeforeRedirection}>
      {/* eslint-disable */}
        {(!cpmProduct || !purchase) ?
        <Error404 withoutTemplate /> :
          <PurchaseWrapper purchase={purchase} product={cpmProduct}>
            <PurchasePayment
              purchase={purchase}
              options={productType.paymentOptions}
              onSuccess={handlePaymentSuccess}
            />
        </PurchaseWrapper>}
        {/* eslint-enable */}
    </LoadingDisplayer>
  );
};

Purchase.propTypes = {
  location: PropTypes.shape({
    query: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }).isRequired,
  cpmProduct: PropTypes.oneOfType([cpmBookingDataShape, cpmOnDemandAccessForCheckoutDataShape]),
  cpmProductLoading: PropTypes.bool.isRequired,

  cpmUpdatedMyBooking: cpmMyBookingPurchasesShape,

  dispatchGetMyBooking: PropTypes.func.isRequired,
  dispatchGetOnDemandAccess: PropTypes.func.isRequired,
  dispatchGetUpdatedMyBooking: PropTypes.func.isRequired,
};

Purchase.defaultProps = {
  cpmProduct: null,
  cpmUpdatedMyBooking: null,
};

const mapStateToProps = (state, ownProps) => {
  const { stateProductPath } = mFinance.findPurchasableProduct(ownProps.location.query);
  return {
    slugId: _get(state, 'cpmSlug.current.result.id'),

    cpmProduct: _get(state, `${stateProductPath}.result`),
    cpmProductLoading: _get(state, `${stateProductPath}.loading`),

    cpmUpdatedMyBooking: _get(state, 'cpmBooking.myBooking.updated.result'),
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps;
  return {
    ...ownProps,
    ...stateProps,

    dispatchGetMyBooking: (data) => dispatch(cpmGetMyBooking(queGetMyBooking, data)),
    dispatchGetUpdatedMyBooking: (data) => dispatch(cpmGetUpdatedMyBooking(queGetUpdatedMyBookingPurchases, data)),

    dispatchGetOnDemandAccess: (data) => dispatch(cpmGetOnDemandAccess(queGetOnDemandAccess, { ...data, slugId: stateProps.slugId })),
  };
};

module.exports = compose(
  connect(mapStateToProps, null, mergeProps),
  withStateReinit(['cpmOnDemand', 'cpmMyBooking', 'myBooking']),
  withTitle('pageTitle.payment'),
)(Purchase);



// WEBPACK FOOTER //
// ./routes/purchase/components/Purchase.js